'use client';

import { useEffect, useState } from 'react';
import { Shield, LogOut, Loader2 } from 'lucide-react';
import { api } from '@/shared/services/api';
import { leaveToFreshPage } from '@/features/auth/stores/auth-store';
import { useNavigationLeave } from '@/shared/components/navigation-guard';

interface ImpersonationInfo {
  originalAdminEmail: string;
  impersonatedUserEmail: string;
}

/**
 * Banner de impersonare — P4 (2026-07-06): sursa de adevar e un cookie httpOnly
 * SEMNAT (`sf_impersonation`, JWT RS256), citit prin GET /auth/impersonation-state.
 *
 * Inainte, bannerul se afisa dintr-un param URL (`?impersonation=1&admin_email=...`)
 * pe care ORICINE il putea forja intr-un link -> banner „Conectat ca admin (X)" fals.
 * Acum apare DOAR daca serverul a setat cookie-ul semnat la o impersonare reala; un
 * param URL sau un cookie forjat (semnatura invalida) nu-l mai pot produce.
 */
export function ImpersonationBanner() {
  const [impersonation, setImpersonation] = useState<ImpersonationInfo | null>(null);
  const [exiting, setExiting] = useState(false);
  const { requestLeave } = useNavigationLeave();

  useEffect(() => {
    let cancelled = false;
    api
      .get<{ impersonating: boolean; adminEmail?: string; impersonatedEmail?: string }>(
        '/auth/impersonation-state',
      )
      .then((res) => {
        if (cancelled) return;
        if (res?.impersonating && res.adminEmail && res.impersonatedEmail) {
          setImpersonation({
            originalAdminEmail: res.adminEmail,
            impersonatedUserEmail: res.impersonatedEmail,
          });
        }
      })
      .catch(() => {
        /* fara impersonare -> fara banner */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleExit = async () => {
    setExiting(true);
    try {
      // Restaureaza sesiunea admin + sterge cookie-ul sf_impersonation (server-side).
      await api.post('/auth/admin-restore');
    } catch (err) {
      console.error('Error restoring admin session:', err);
    }
    // Full redirect to admin — forces React state reset with restored admin cookies. Abia după
    // „se încarcă”: altfel un formular nesalvat al contului vizualizat rămânea (la „Rămâi”) pe
    // ecran, iar salvarea lui pleca deja cu sesiunea adminului.
    await leaveToFreshPage(() => {
      window.location.href = '/admin/users';
    });
  };

  if (!impersonation) return null;

  return (
    <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white sticky top-0 z-[100]">
      <div className="mx-auto px-4 py-2.5">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1">
            <div className="bg-white/20 p-1.5 rounded-lg">
              <Shield className="h-4 w-4" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-sm">
                Vizualizezi contul: <span className="font-bold">{impersonation.impersonatedUserEmail}</span>
              </p>
              <p className="text-[0.6875rem] text-blue-100">
                Conectat ca admin ({impersonation.originalAdminEmail}). Apasă „Ieșire din cont" ca să revii la contul tău de administrator.
              </p>
            </div>
          </div>
          <button
            onClick={() => requestLeave(() => void handleExit(), { actionLabel: 'Ieși din cont' })}
            disabled={exiting}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white text-blue-600 hover:bg-blue-50 text-sm font-medium transition-colors disabled:opacity-50"
          >
            {exiting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <LogOut className="h-4 w-4" />
            )}
            {exiting ? 'Se restaureaza...' : 'Iesire din cont'}
          </button>
        </div>
      </div>
    </div>
  );
}
